// components/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [emailcode, setEmailcode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1); // 1: email, 2: code & password
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSuccess(data.message || 'Verification code sent to your email');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/auth/reset-password', {
        email,
        emailcode,
        newPassword
      });
      setSuccess(data.message || 'Password reset successfully!');
      setError('');
      // Clear form after 2 seconds and redirect to login
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      setSuccess('New verification code sent to your email');
      setEmailcode(''); // Clear the old code
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto -mt-12 p-6 bg-gray-800 rounded-lg shadow-lg font-mono">
      <h2 className="text-2xl font-bold mb-4 text-white text-center">
        Forgot Password
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded text-red-300 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded text-green-300 text-sm">
          {success}
        </div>
      )}

      {step === 1 ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <p className="text-gray-400 text-sm mb-4">
            Enter your email address and we'll send you a verification code to reset your password.
          </p>
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-gradient-to-r from-violet-500 to-pink-500 rounded text-white font-semibold hover:from-violet-600 hover:to-pink-600 transition relative disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                <span>Sending...</span>
              </div>
            ) : (
              'Send Verification Code'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-gray-400 text-sm mb-4">
            Enter the 6-digit code sent to {email} and your new password.
          </p>

          <input
            type="text"
            placeholder="6-digit verification code"
            value={emailcode}
            onChange={(e) => setEmailcode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            maxLength="6"
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500 text-center text-lg tracking-widest"
          />

          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />

          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full px-3 py-2 bg-gray-700 text-white rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-gradient-to-r from-violet-500 to-pink-500 rounded text-white font-semibold hover:from-violet-600 hover:to-pink-600 transition relative disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                <span>Resetting...</span>
              </div>
            ) : (
              'Reset Password'
            )}
          </button>

          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading}
            className="w-full py-2 bg-gray-700 rounded text-gray-300 hover:bg-gray-600 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Resend Code
          </button>

          <button
            type="button"
            onClick={() => {
              setStep(1);
              setError('');
              setSuccess('');
              setEmailcode('');
              setNewPassword('');
              setConfirmPassword('');
            }}
            className="w-full py-2 bg-transparent border border-gray-600 rounded text-gray-400 hover:border-gray-500 transition text-sm"
          >
            Change Email
          </button>
        </form>
      )}

      <div className="mt-4 text-sm text-gray-400 text-center space-y-2">
        <p>
          <Link to="/login" className="text-violet-400 hover:text-violet-200">
            Back to Login
          </Link>
        </p>
        {step === 1 && (
          <p>
            Need an account?{' '}
            <Link to="/signup" className="text-violet-400 hover:text-violet-200">
              Sign Up
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
